/**
 * Copyright 2022 (C) Blink - All Rights Reserved
 * 
 * This source code is protected under international copyright law.  All rights
 * reserved and protected by the copyright holders.
 * All files are confidential and only available to authorized individuals with the
 * permission of the copyright holders. If you encounter any file and do not have
 * permission, please get in touch with the copyright holders and delete this file.
 */
import warning from "utils/warning";

interface ITryCatchOptions {
  onError?: (error: Error | unknown) => void;
  onFinally?: ({ isError, payload }: any) => any;
  setLoading?: (isLoading: boolean) => void;
  fieldsToValidate?: { [key in string]: any } | null;
  isAsync?: boolean;
}

type Ifn = (...args: any[]) => any;
type IOptions = ITryCatchOptions | ((isLoading: boolean) => void);

const useTryCatch = (fn: Ifn, options?: IOptions) => {
  const settings: ITryCatchOptions = {
    setLoading: undefined,
    onFinally: undefined,
    onError: undefined,
    isAsync: true,
  };
  if (options && typeof options === "object") {
    Object.assign(settings, options);
  }
  if (options && typeof options === "function") {
    settings.setLoading = options;
  }
  const { onError, onFinally, setLoading, fieldsToValidate, isAsync } =
    settings;
  // basically the idea is to wrap the function declaration with `useTryCatch` which will return an augmented function with a try-catch block
  if (isAsync) {
    return async (...args: any[]) => {
      let isError = false,
        payload;
      try {
        if (setLoading) setLoading(true);
        payload = await fn(...args);
      } catch (error) {
        warning(error);
        if (onError) onError(error);
        isError = true;
      } finally {
        if (setLoading) setLoading(false);
        if (onFinally) return onFinally({ isError, payload });
        return payload;
      }
    };
  }
  return (...args: any[]) => {
    let isError = false,
      payload;
    try {
      if (setLoading) setLoading(true);
      payload = fn(...args);
    } catch (error) {
      warning(error);
      if (onError) onError(error);
      isError = true;
    } finally {
      if (setLoading) setLoading(false);
      if (onFinally) return onFinally({ isError, payload });
      return payload;
    }
  };
};

export const tryCatcher = (
  fn: (...args: any[]) => any,
  options?: ITryCatchOptions
) => {
  const settings: ITryCatchOptions = {
    onError: undefined,
    onFinally: undefined,
    isAsync: false,
  };
  if (options && typeof options === "object") {
    Object.assign(settings, options);
  }
  const { onError, onFinally, isAsync } = settings;
  if (isAsync) {
    return async (...args: any[]) => {
      let isError = false,
        payload;
      try {
        payload = await fn(...args);
      } catch (error) {
        warning(error);
        if (onError) onError(error);
        isError = true;
      } finally {
        if (onFinally) return onFinally({ isError, payload });
        return payload;
      }
    };
  }
  return (...args: any[]) => {
    let isError = false,
      payload;
    try {
      payload = fn(...args);
    } catch (error) {
      warning(error);
      if (onError) onError(error);
      isError = true;
    } finally {
      if (onFinally) return onFinally({ isError, payload });
      return payload;
    }
  };
};

export default useTryCatch;
